// pages/TravelExpense/travelPrint.js
//
// Go-live parity (2026-09-24): audit 09-travel #20 / #21 — the two legacy
// prints, rebuilt as browser print pages (the user prints or saves as PDF
// from the browser dialog):
//   • printTea(detail)          legacy "Print TEA" (Crystal TravelApprovalRpt):
//                               trip header, proposer, travelers, status,
//                               destinations, estimated expenses, approval log.
//   • printFullReport(id, …)    legacy "Print Entire Report" (DevExpress
//                               TravelReport1): the TEA plus every traveler's
//                               actual expenses in the 8 categories and the
//                               per-destination trip reports.
// Data comes from the same endpoints the pages use; nothing is stored.

import api from "../../api";
import { toCamel } from "./travelCase";
import { statusText } from "./travelStatus";

const esc = (v) => String(v ?? "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
const fmtDate = (d) => { if (!d) return "—"; try { return new Date(d).toLocaleDateString("en-US"); } catch { return "—"; } };
const fmtDt = (d) => { if (!d) return "—"; try { return new Date(d).toLocaleString("en-US"); } catch { return "—"; } };
const money = (v) => {
    const n = typeof v === "number" ? v : parseFloat(v);
    return Number.isFinite(n) ? n.toLocaleString("en-US", { style: "currency", currency: "USD" }) : "—";
};

const CSS = `
  body { font-family: Arial, Helvetica, sans-serif; font-size: 12px; color: #000; margin: 24px; }
  h1 { font-size: 18px; margin: 0 0 4px; } h2 { font-size: 14px; margin: 18px 0 6px; border-bottom: 1px solid #000; }
  h3 { font-size: 13px; margin: 12px 0 4px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
  th, td { border: 1px solid #999; padding: 3px 5px; text-align: left; vertical-align: top; }
  th { background: #eee; } td.r, th.r { text-align: right; }
  .meta td { border: none; padding: 2px 6px 2px 0; } .muted { color: #555; }
  .pre { white-space: pre-wrap; } .page { page-break-before: always; }
  @media print { body { margin: 0; } }
`;

// Opened synchronously from the click so popup blockers allow it; filled later.
const openWindow = (title) => {
    const w = window.open("", "_blank");
    if (!w) return null;
    w.document.write(`<!doctype html><html><head><title>${esc(title)}</title><style>${CSS}</style></head><body><p>Loading…</p></body></html>`);
    w.document.close();
    return w;
};

const fill = (w, title, body) => {
    w.document.open();
    w.document.write(`<!doctype html><html><head><title>${esc(title)}</title><style>${CSS}</style></head><body>${body}</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => { try { w.print(); } catch { /* user can print manually */ } }, 300);
};

const teaHtml = (d) => {
    const t = d.trip || {};
    const total = ["airfare", "lodging", "meals", "entertainment", "carRental", "personalCar", "taxi", "other"]
        .reduce((a, k) => a + (Number(t[k]) || 0), 0);
    const status = t.status === 2 && d.approverName
        ? `Awaiting approval of ${d.approverName}`
        : statusText(t.status, d.statusLabel);
    const dests = (d.destinations || []).map((x) => `<tr>
        <td>${fmtDate(x.startDate)}</td><td>${fmtDate(x.endDate)}</td><td class="r">${esc(x.numberOfHours)}</td>
        <td>${esc(x.natureOfVisit)}</td><td>${esc(x.company)}</td><td>${esc(x.cityStateCountry)}</td>
        <td>${esc(x.contactPerson)}</td><td>${esc(x.goal)}</td></tr>`).join("");
    const log = (d.log || []).map((l) => `<tr><td>${esc(l.status)}</td><td>${esc(l.name || (l.empid ? l.empid : ""))}</td>
        <td>${fmtDt(l.stamp)}</td><td>${esc(l.comment)}</td></tr>`).join("");
    const est = [["Airfare", t.airfare], ["Lodging", t.lodging], ["Meals", t.meals], ["Entertainment", t.entertainment],
        ["Car Rental", t.carRental], ["Personal Car", t.personalCar], ["Taxi", t.taxi], ["Misc Expenses", t.other]]
        .map(([k, v]) => `<tr><td>${k}</td><td class="r">${money(v)}</td></tr>`).join("");
    return `
      <h1>Travel Expense Approval — #${esc(t.id)}</h1>
      <div class="muted">SS White Technologies</div>
      <table class="meta">
        <tr><td><b>Name of Trip:</b></td><td>${esc(t.nameOfTrip)}</td><td><b>Status:</b></td><td>${esc(status)}</td></tr>
        <tr><td><b>Proposed By:</b></td><td>${esc(d.requesterName)}</td><td><b>Date Prepared:</b></td><td>${fmtDate(t.submitDate)}</td></tr>
        <tr><td><b>Leave Home:</b></td><td>${fmtDate(t.leaveHomeDate)}</td><td><b>Return Home:</b></td><td>${fmtDate(t.returnHomeDate)}</td></tr>
        <tr><td><b>Travelers:</b></td><td colspan="3">${esc((d.travelers || []).map((x) => x.name).join(", "))}</td></tr>
      </table>
      <h2>Primary Objective</h2><div class="pre">${esc(t.primaryObjective)}</div>
      <h2>Destinations</h2>
      <table><thead><tr><th>Start</th><th>End</th><th class="r">Hours</th><th>Nature of Visit</th><th>Company</th>
        <th>City, State, Country</th><th>Contact</th><th>Goal</th></tr></thead>
        <tbody>${dests || `<tr><td colspan="8" class="muted">No destinations.</td></tr>`}</tbody></table>
      <h2>Estimated Expenses (for the entire group)</h2>
      <table style="width:50%"><tbody>${est}
        <tr><th>Estimated Total</th><th class="r">${money(total)}</th></tr></tbody></table>
      ${t.otherNote ? `<div><b>Misc description:</b> ${esc(t.otherNote)}</div>` : ""}
      <h2>Approval History</h2>
      <table><thead><tr><th>Action</th><th>By</th><th>When</th><th>Comment</th></tr></thead>
        <tbody>${log || `<tr><td colspan="4" class="muted">No entries.</td></tr>`}</tbody></table>`;
};

export const printTea = (detail) => {
    const title = `TEA ${detail?.trip?.id ?? ""}`;
    const w = openWindow(title);
    if (!w) return false;
    fill(w, title, teaHtml(detail));
    return true;
};

const CATEGORY_LABELS = {
    air: "Airfare", lodging: "Lodging", meals: "Meals", entertainment: "Entertainment",
    carRental: "Car Rental", personalCar: "Personal Car", taxi: "Taxi", misc: "Misc Expenses",
};

const detailsOf = (r) => {
    switch (r.category) {
        case "air": return [r.airline, r.departLocation && `${r.departLocation} → ${r.landLocation || ""}`, r.roundTrip ? "round trip" : null].filter(Boolean).join(", ");
        case "lodging": return [r.hotelName, r.endDate && `to ${fmtDate(r.endDate)}`].filter(Boolean).join(", ");
        case "meals": return [r.restaurant, r.city, r.numberOfPeople != null && `${r.numberOfPeople} people`, r.guestNames].filter(Boolean).join(", ");
        case "entertainment": return [r.eventDescription, r.companyName, r.numberOfPeople != null && `${r.numberOfPeople} people`, r.guestNames].filter(Boolean).join(", ");
        case "carRental": return [r.rentalOutfit, r.rentalCity, r.endDate && `to ${fmtDate(r.endDate)}`].filter(Boolean).join(", ");
        case "personalCar": return [r.fromCity && `${r.fromCity} → ${r.toCity || ""}`, r.milesTraveled != null && `${r.milesTraveled} mi × ${money(r.mileageRate)}`,
            Number(r.tollsAmount) ? `tolls ${money(r.tollsAmount)}` : null, Number(r.parkingAmount) ? `parking ${money(r.parkingAmount)}` : null].filter(Boolean).join(", ");
        case "taxi": return [r.fromCity && `${r.fromCity} → ${r.toCity || ""}`].filter(Boolean).join(", ");
        case "misc": return r.expenseDetails || "";
        default: return "";
    }
};

const expensesHtml = (e) => {
    const items = e.items || [];
    const cats = Object.keys(CATEGORY_LABELS);
    let grand = 0;
    const sections = cats.map((c) => {
        const rows = items.filter((r) => r.category === c);
        if (rows.length === 0) return "";
        const sum = rows.reduce((a, r) => a + (Number(r.cost) || 0), 0);
        grand += sum;
        return `<h3>${CATEGORY_LABELS[c]}</h3>
          <table><thead><tr><th>Date</th><th>Details</th><th>Paid</th><th>Comment</th><th class="r">Amount</th><th class="r">Traveler share</th></tr></thead>
          <tbody>${rows.map((r) => `<tr><td>${fmtDate(r.date)}</td><td>${esc(detailsOf(r))}${r.group ? " <i>(group)</i>" : ""}</td>
            <td>${esc(r.paymentSource)}${r.cardLastFour ? ` ****${esc(r.cardLastFour)}` : ""}</td><td>${esc(r.comment)}</td>
            <td class="r">${money(r.amount)}</td><td class="r">${money(r.cost)}</td></tr>`).join("")}
            <tr><th colspan="5">Total ${CATEGORY_LABELS[c]}</th><th class="r">${money(sum)}</th></tr></tbody></table>`;
    }).join("");
    const t = e.traveler || {};
    return `<h2>Actual Expenses — ${esc(t.name)}${t.empSubmitted ? " (submitted)" : " (not submitted)"}</h2>
      ${sections || `<div class="muted">No expenses entered.</div>`}
      <div><b>Total actual expenses (traveler share): ${money(grand)}</b></div>`;
};

const reportsHtml = (r) => {
    const items = r.items || [];
    if (items.length === 0) return "";
    return `<h2>Trip Report</h2>` + items.map((x) => `
      <h3>${esc(x.company)} — ${esc(x.cityStateCountry)} (${fmtDate(x.startDate)} – ${fmtDate(x.endDate)})</h3>
      ${x.reportId ? `
        <div><b>Overview</b></div><div class="pre">${esc(x.overview)}</div>
        <div><b>Contacts</b></div><div class="pre">${esc(x.contacts)}</div>
        <div><b>Notes</b></div><div class="pre">${esc(x.notes)}</div>
        <div><b>Summary</b></div><div class="pre">${esc(x.summary)}</div>
        <div class="muted">Last saved by ${esc(x.lastSavedByName || x.lastSavedBy)}</div>`
        : `<div class="muted">No report written for this destination.</div>`}`).join("");
};

// Legacy printed one report per traveler; here the viewer gets every
// traveler they may see (their own, or all of them for approver /
// supervisor / HR / Accounting / MPC / IT Admin).
export const printFullReport = async (tripId, detail) => {
    const title = `Travel Report ${tripId}`;
    const w = openWindow(title);
    if (!w) return false;
    try {
        const ws = toCamel((await api.get(`TravelExpense/${tripId}/workspace`)).data) || {};
        const viewer = ws.viewer || {};
        const travelers = (ws.travelers || []).filter((t) => viewer.oversight || t.empid === viewer.empId);
        const expenses = [];
        for (const t of travelers) {
            try {
                expenses.push(toCamel((await api.get(`TravelExpense/${tripId}/expenses`, { params: { empId: t.empid } })).data));
            } catch { /* skip a traveler the viewer may not see */ }
        }
        let reports = {};
        try { reports = toCamel((await api.get(`TravelExpense/${tripId}/reports`)).data) || {}; } catch { reports = {}; }
        const body = teaHtml(detail)
            + expenses.map((e) => `<div class="page">${expensesHtml(e)}</div>`).join("")
            + (reports.items && reports.items.length ? `<div class="page">${reportsHtml(reports)}</div>` : "");
        fill(w, title, body);
    } catch (e) {
        fill(w, title, `<p>Could not build the report: ${esc(e?.response?.data?.error || e?.message || "error")}</p>`);
    }
    return true;
};
